import type { ProgramCategoryCode } from '@/config/program-category';
import { cn } from '@/lib/utils';

import { CategoryIcon } from './CategoryIcon';

interface ProgramCategoryTagProps {
  /** 분류축 CATEGORY 코드 — 없으면(미분류) 원문 `fallbackLabel` 만 아이콘 없이 보인다. */
  code: null | ProgramCategoryCode;
  /** 유형 표시명(CATEGORY 노드 name — 서버 응답). */
  name?: null | string;
  /** 미분류일 때 보일 원문 분류. */
  fallbackLabel?: null | string;
  /** 관심 유형 강조 — 행에서 이 유형만 칩으로 띄운다. */
  emphasized?: boolean;
  className?: string;
}

/**
 * 사업 유형 표기 — 아이콘 + 이름. 좁은 화면(sm 미만)은 아이콘만 남기고 이름은 스크린리더·툴팁(title)으로 전달한다.
 * 기본은 무채색, 관심 유형만 차트 초록(chart-4) 칩 — 같은 행의 판정(accent·primary·warning)·마감(warning·destructive)
 * 색과 겹치지 않는 범주색을 쓴다.
 */
export function ProgramCategoryTag({
  code,
  name,
  fallbackLabel,
  emphasized,
  className,
}: ProgramCategoryTagProps) {
  const label = (code && name) || fallbackLabel;
  if (!label) return null;
  if (!code || !name) {
    return (
      <span
        className={cn(
          'hidden shrink-0 truncate text-sm text-muted-foreground sm:inline',
          className,
        )}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-sm',
        emphasized
          ? 'rounded-full border border-chart-4/25 bg-chart-4/10 px-2 py-0.5 font-medium text-chart-4'
          : 'text-muted-foreground',
        className,
      )}
    >
      <CategoryIcon code={code} className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="sr-only sm:not-sr-only">{label}</span>
    </span>
  );
}
